import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  X, ArrowClockwise, WarningCircle, Eye, CheckCircle, Broom, CaretRight,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UNREAD_META = {
  deposit: { label: 'Deposit', short: 'D', color: '#2563eb' },
  withdrawal: { label: 'Withdraw', short: 'W', color: '#dc2626' },
  support: { label: 'Support', short: 'S', color: '#0d9488' },
  inquiry: { label: 'Inquiry', short: 'I', color: '#7c3aed' },
};

/**
 * PendingManager — one place that explains & clears EVERY lit toggle
 * (S / W / Deposit / Inquiry unread, plus PW / PT / N / G). Shows what each
 * toggle is, opens the source, and lets you bulk clear / mark-solved. Also
 * surfaces "orphan pending" (non-active rooms that still hold an unread
 * customer message — the invisible phantom-badge culprit).
 */
const PendingManager = ({ user, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Pass the current path as `from` so the chat/group back button REPLACES the
  // chat in history (navigate(from,{replace:true})) instead of pushing the
  // dashboard on top — otherwise pressing back again jumped straight back INTO
  // the chat (history loop the user reported).
  const fromPath = (location.pathname || '') + (location.search || '');
  const backState = { from: fromPath && fromPath.startsWith('/') ? fromPath : undefined };
  const isHandler = String(user?.role || '').includes('handler');
  const chatPath = (roomId) => (isHandler ? `/handler-chat/${roomId}` : `/admin-chat/${roomId}`);

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [unread, setUnread] = useState({}); // {type: {items, active_count, orphan_count}}
  const [nData, setNData] = useState({ count: 0, chats: [] });
  const [pw, setPw] = useState({ count: 0, items: [] });
  const [pt, setPt] = useState({ count: 0, tasks: [] });
  const [g, setG] = useState({ total: 0, groups: [] });
  const [busy, setBusy] = useState(null); // action key currently running
  const [confirm, setConfirm] = useState(null); // { title, msg, onYes }

  const loadAll = useCallback(async () => {
    if (!user?.id) return;
    const uid = user.id;
    // Instant open: paint the shell right away and let EACH section fill in
    // independently as its own request resolves. Previously we awaited all 5
    // together, so one slow/stuck request froze the whole panel for 30-40s.
    setLoading(false);
    setRefreshing(true);
    const jobs = [
      axios.get(`${API}/chat/pending-unread-detail`, { params: { user_id: uid } })
        .then((r) => setUnread(r.data.detail || {})).catch(() => {}),
      axios.get(`${API}/chats/new-members`, { params: { user_id: uid } })
        .then((r) => setNData(r.data || { count: 0, chats: [] })).catch(() => {}),
      axios.get(`${API}/withdraw-entries/pending-list`, { params: { user_id: uid, threshold_minutes: 0 } })
        .then((r) => setPw(r.data || { count: 0, items: [] })).catch(() => {}),
      axios.get(`${API}/chats/pending-tasks`, { params: { user_id: uid, threshold_minutes: 0 } })
        .then((r) => setPt(r.data || { count: 0, tasks: [] })).catch(() => {}),
      axios.get(`${API}/mb-groups/pending-summary`, { params: { user_id: uid } })
        .then((r) => setG(r.data || { total: 0, groups: [] })).catch(() => {}),
    ];
    await Promise.allSettled(jobs);
    setRefreshing(false);
  }, [user?.id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const goChat = (roomId, chatType) => {
    onClose && onClose();
    navigate(chatPath(roomId), { state: { activeTab: chatType, chatTypeTab: chatType, ...backState } });
  };

  const clearUnread = async (type, mode) => {
    setBusy(`${type}-${mode}`);
    try {
      const res = await axios.post(`${API}/chat/clear-pending`, { user_id: user.id, chat_type: type, mode });
      toast.success(`${UNREAD_META[type].label}: cleared ${res.data.cleared_messages} msg(s)` + (res.data.resolved_sessions ? `, ${res.data.resolved_sessions} solved` : ''));
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear');
    } finally {
      setBusy(null);
      setConfirm(null);
    }
  };

  const resolveAllPT = async () => {
    setBusy('pt-all');
    try {
      const tasks = pt.tasks || [];
      await Promise.allSettled(tasks.map((t) => axios.post(`${API}/chats/pending-tasks/resolve`, { room_id: t.room_id, actor_id: user.id })));
      toast.success(`Resolved ${tasks.length} pending task(s)`);
      await loadAll();
    } catch (e) {
      toast.error('Failed to resolve some tasks');
    } finally {
      setBusy(null);
      setConfirm(null);
    }
  };

  const totalPending =
    Object.values(unread).reduce((s, v) => s + (v.total || 0), 0) +
    (nData.count || 0) + (pw.count || 0) + (pt.count || 0) + (g.total || 0);

  return (
    <div className="fixed inset-0 bg-black/60 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="pending-manager">
      <div className="bg-slate-50 w-full sm:max-w-2xl h-[92vh] sm:h-[88vh] rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-slate-800 text-white flex-shrink-0">
          <div>
            <h2 className="text-base font-bold flex items-center gap-2">
              <Broom size={18} weight="bold" /> Pending Manager
            </h2>
            <p className="text-[11px] text-slate-300 mt-0.5">{totalPending} pending item(s) driving your toggles</p>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={loadAll} className="p-2 hover:bg-white/15 rounded-full" data-testid="pm-refresh" title="Refresh">
              <ArrowClockwise size={20} weight="bold" className={refreshing ? 'animate-spin' : ''} />
            </button>
            <button onClick={() => onClose && onClose()} className="p-2 hover:bg-white/15 rounded-full" data-testid="pm-close">
              <X size={22} weight="bold" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {loading ? (
            <p className="text-center text-slate-400 py-10 text-sm">Loading pending items…</p>
          ) : (
            <>
              {/* Unread section toggles: S / W / Deposit / Inquiry */}
              {['support', 'withdrawal', 'deposit', 'inquiry'].map((type) => {
                const d = unread[type];
                if (!d) return null;
                const meta = UNREAD_META[type];
                const items = d.items || [];
                return (
                  <div key={type} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`pm-section-${type}`}>
                    <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <span className="w-7 h-7 rounded-full text-white text-xs font-black flex items-center justify-center" style={{ backgroundColor: meta.color }}>{meta.short}</span>
                        <div>
                          <span className="text-sm font-bold text-slate-800">{meta.label}</span>
                          <span className="text-[11px] text-slate-500 ml-2">{d.total} pending{d.orphan_count ? ` · ${d.orphan_count} orphan` : ''}</span>
                        </div>
                      </div>
                      {items.length > 0 && (
                        <div className="flex items-center gap-1.5">
                          {(type === 'support' || type === 'inquiry') ? (
                            <button
                              onClick={() => setConfirm({ title: `Mark all ${meta.label} solved?`, msg: `This marks all ${d.total} unread ${meta.label} chat(s) as read/solved and clears the toggle.`, onYes: () => clearUnread(type, 'mark_solved') })}
                              disabled={busy === `${type}-mark_solved`}
                              className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1"
                              data-testid={`pm-solve-all-${type}`}
                            >
                              <CheckCircle size={13} weight="bold" /> Mark all solved
                            </button>
                          ) : (
                            <button
                              onClick={() => setConfirm({ title: `Clear all ${meta.label}?`, msg: `This marks all ${d.total} unread ${meta.label} message(s) as read and clears the toggle.`, onYes: () => clearUnread(type, 'clear') })}
                              disabled={busy === `${type}-clear`}
                              className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1"
                              data-testid={`pm-clear-all-${type}`}
                            >
                              <Broom size={13} weight="bold" /> Clear all
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {items.length === 0 ? (
                      <p className="px-3 py-4 text-xs text-slate-400 text-center">Nothing pending ✓</p>
                    ) : (
                      <div className="divide-y divide-slate-100 max-h-56 overflow-y-auto">
                        {items.map((it) => (
                          <div key={it.room_id} className="flex items-center gap-2 px-3 py-2" data-testid={`pm-item-${it.room_id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-medium text-slate-800 truncate">{it.customer_name}</span>
                                {it.orphan && (
                                  <span className="text-[9px] font-bold px-1.5 py-[1px] rounded bg-orange-100 text-orange-700 uppercase flex items-center gap-0.5" title="Chat is no longer active but still holds an unread message — the phantom-badge source">
                                    <WarningCircle size={10} weight="bold" /> orphan
                                  </span>
                                )}
                              </div>
                              <span className="text-[11px] text-slate-500">{it.unread_count} unread · {it.customer_phone || '—'}</span>
                            </div>
                            <button
                              onClick={() => goChat(it.room_id, it.chat_type)}
                              className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-lg bg-sky-100 text-sky-700 hover:bg-sky-200"
                              data-testid={`pm-open-${it.room_id}`}
                            >
                              <Eye size={13} weight="bold" /> Open
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* PT — pending tasks (awaiting reply) */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="pm-section-pt">
                <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-amber-500 text-white text-[11px] font-black flex items-center justify-center">PT</span>
                    <div><span className="text-sm font-bold text-slate-800">Pending Tasks</span><span className="text-[11px] text-slate-500 ml-2">{pt.count || 0} awaiting reply</span></div>
                  </div>
                  {(pt.tasks || []).length > 0 && (
                    <button onClick={() => setConfirm({ title: 'Resolve all pending tasks?', msg: `Marks all ${pt.count} awaiting-reply chats as handled.`, onYes: resolveAllPT })}
                      disabled={busy === 'pt-all'}
                      className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1" data-testid="pm-resolve-all-pt">
                      <CheckCircle size={13} weight="bold" /> Resolve all
                    </button>
                  )}
                </div>
                {(pt.tasks || []).length === 0 ? (
                  <p className="px-3 py-4 text-xs text-slate-400 text-center">All caught up ✓</p>
                ) : (
                  <div className="divide-y divide-slate-100 max-h-56 overflow-y-auto">
                    {pt.tasks.map((t) => (
                      <div key={t.room_id} className="flex items-center gap-2 px-3 py-2">
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-slate-800 truncate block">{t.customer_name}</span>
                          <span className="text-[11px] text-slate-500 truncate block">{t.last_message || '(media)'}</span>
                        </div>
                        <button onClick={() => goChat(t.room_id, t.chat_type)} className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-lg bg-sky-100 text-sky-700 hover:bg-sky-200">
                          <Eye size={13} weight="bold" /> Open
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* PW — pending withdrawals */}
              <PanelRow label="Pending Withdrawals" short="PW" color="#dc2626" count={pw.count || 0}
                emptyText="All withdraws paid ✓" testid="pm-section-pw"
                items={(pw.items || []).map((it) => ({
                  key: it.entry_id,
                  title: it.customer_name,
                  sub: `₹ ${Number(it.amount || 0).toLocaleString('en-IN')}`,
                  onOpen: () => { onClose && onClose(); navigate(it.group_message_id ? `/groups/${it.group_id}?scroll_to=${encodeURIComponent(it.group_message_id)}` : `/groups/${it.group_id}`, { state: backState }); },
                }))} />

              {/* N — new members without MB */}
              <PanelRow label="New Members (no MB)" short="N" color="#0ea5e9" count={nData.count || 0}
                emptyText="No new members ✓" testid="pm-section-n"
                headerAction={(nData.chats || []).length > 0 ? { text: 'Open all', onClick: () => { onClose && onClose(); navigate('/new-members'); } } : null}
                items={(nData.chats || []).slice(0, 50).map((c) => ({
                  key: c.customer_id,
                  title: c.customer_name,
                  sub: `${(c.rooms || []).length} chat(s)`,
                  onOpen: () => { onClose && onClose(); navigate('/new-members'); },
                }))} />

              {/* G — withdraw groups pending */}
              <PanelRow label="Withdraw Groups Pending" short="G" color="#00a884" count={g.total || 0}
                emptyText="No group pending ✓" testid="pm-section-g"
                items={(g.groups || []).filter((x) => (x.pending || 0) > 0).map((x) => ({
                  key: x.mb_setup,
                  title: x.mb_setup,
                  sub: `${x.pending} pending`,
                  onOpen: () => { onClose && onClose(); navigate(`/groups/${x.group_id}`, { state: backState }); },
                }))} />
            </>
          )}
        </div>
      </div>

      {/* Confirm popup */}
      {confirm && (
        <div className="fixed inset-0 bg-black/50 z-[90] flex items-center justify-center p-4" data-testid="pm-confirm">
          <div className="bg-white rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <h3 className="text-base font-bold text-slate-800 mb-2">{confirm.title}</h3>
            <p className="text-sm text-slate-600 mb-4">{confirm.msg}</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirm(null)} className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100 font-medium">Cancel</button>
              <button onClick={confirm.onYes} disabled={!!busy} className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 font-medium" data-testid="pm-confirm-yes">
                {busy ? 'Working…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PanelRow = ({ label, short, color, count, items, emptyText, testid, headerAction }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={testid}>
    <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100">
      <div className="flex items-center gap-2">
        <span className="w-7 h-7 rounded-full text-white text-[11px] font-black flex items-center justify-center" style={{ backgroundColor: color }}>{short}</span>
        <div><span className="text-sm font-bold text-slate-800">{label}</span><span className="text-[11px] text-slate-500 ml-2">{count} pending</span></div>
      </div>
      {headerAction && (
        <button onClick={headerAction.onClick} className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center gap-1">
          {headerAction.text} <CaretRight size={12} weight="bold" />
        </button>
      )}
    </div>
    {items.length === 0 ? (
      <p className="px-3 py-4 text-xs text-slate-400 text-center">{emptyText}</p>
    ) : (
      <div className="divide-y divide-slate-100 max-h-56 overflow-y-auto">
        {items.map((it) => (
          <div key={it.key} className="flex items-center gap-2 px-3 py-2">
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-slate-800 truncate block">{it.title}</span>
              <span className="text-[11px] text-slate-500">{it.sub}</span>
            </div>
            <button onClick={it.onOpen} className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-lg bg-sky-100 text-sky-700 hover:bg-sky-200">
              <Eye size={13} weight="bold" /> Open
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default PendingManager;
